import React, {memo, useEffect, useRef, useState} from 'react';
import {useAutoplayAllowed} from '@/hooks/useAutoplayAllowed';
import Image from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import SectionHeader from '@/components/molecules/SectionHeader';
import styles from './ComplianceStandards.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ComplianceStandard {
    /** Standard or framework name, e.g. `GDPR`. */
    name: string;
    /** One-line explanation of what the standard covers. */
    description: string;
    /** Optional certification badge. Falls back to the layer icon tile. */
    image?: string;
}

export interface ComplianceRow {
    iconType: string;
    layer: string;
    standards: ComplianceStandard[];
}

export interface ComplianceStandardsProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    rows: ComplianceRow[];
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const strokeProps = {
    width: '18',
    height: '18',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
} as const;

const ShieldIcon = () => (
    <svg {...strokeProps}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

const KeyIcon = () => (
    <svg {...strokeProps}>
        <circle cx="7" cy="17" r="3" />
        <path d="M9.59 14.41L21 3" />
        <path d="M18 5l2 2" />
        <path d="M15 8l2 2" />
    </svg>
);

const CodeIcon = () => (
    <svg {...strokeProps}>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
    </svg>
);

const CardIcon = () => (
    <svg {...strokeProps}>
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
);

const GearIcon = () => (
    <svg {...strokeProps}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const ChainIcon = () => (
    <svg {...strokeProps}>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);

const ChartIcon = () => (
    <svg {...strokeProps}>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
);

const DatabaseIcon = () => (
    <svg {...strokeProps}>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
);

const iconMap: Record<string, React.ReactElement> = {
    shield: <ShieldIcon />,
    key: <KeyIcon />,
    code: <CodeIcon />,
    card: <CardIcon />,
    gear: <GearIcon />,
    chain: <ChainIcon />,
    chart: <ChartIcon />,
    database: <DatabaseIcon />
};

const AUTOPLAY_INTERVAL_MS = 4000;

// ─── StandardCard ─────────────────────────────────────────────────────────────

const StandardCard = memo(({standard, fallbackIcon}: {standard: ComplianceStandard; fallbackIcon: React.ReactElement}) => (
    <div className={styles.standardCard}>
        {standard.image ? (
            <div className={styles.badgeWrap}>
                <Image
                    src={standard.image}
                    alt={`${standard.name} compliance badge`}
                    fill
                    sizes="(max-width: 900px) 56px, 80px"
                    style={{objectFit: 'contain'}}
                />
            </div>
        ) : (
            <div className={styles.iconWrap}>{fallbackIcon}</div>
        )}
        <div>
            <p className={`font-headers ${styles.standardName}`}>{standard.name}</p>
            <p className={`font-regular-text ${styles.standardDesc}`}>{standard.description}</p>
        </div>
    </div>
));
StandardCard.displayName = 'StandardCard';

// ─── Main component ───────────────────────────────────────────────────────────

// Phones lay the tabs and panels out as one list (see the `down(md)` block in the stylesheet): tab i takes
// order 2i and its panel 2i + 1, so the open panel sits under its tab. On desktop both lists keep this
// relative order, so it changes nothing there.
const tabOrder = (index: number): React.CSSProperties => ({order: index * 2});
const panelOrder = (index: number): React.CSSProperties => ({order: index * 2 + 1});

const ComplianceStandards = ({eyebrow, heading, subheading, rows}: ComplianceStandardsProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    // Desktop only: on phones the active layer's panel opens under its tab, so rotating it moves
    // everything below (layout shift). Also stops while the section is off screen.
    const autoplayAllowed = useAutoplayAllowed(sectionRef);

    useEffect(() => {
        if (!autoplayAllowed || isPaused || rows.length <= 1) return undefined;

        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % rows.length);
        }, AUTOPLAY_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [autoplayAllowed, isPaused, rows.length]);

    return (
        <section ref={sectionRef} className="top-space mobile-top-padding bottom-spacing mobile-padding-bottom">
            <Container>
                <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading ? parse(subheading) : undefined} />

                <div className={styles.shell}>
                    {/* Coverage layers */}
                    <div className={styles.tabList} role="tablist" aria-label="Compliance coverage layers">
                        {rows.map((row, i) => (
                            <button
                                key={row.layer}
                                type="button"
                                role="tab"
                                id={`compliance-tab-${i}`}
                                style={tabOrder(i)}
                                aria-selected={i === activeIndex}
                                aria-controls={`compliance-panel-${i}`}
                                onClick={() => {
                                    setActiveIndex(i);
                                    setIsPaused(true);
                                }}
                                className={`font-headers ${styles.tab} ${i === activeIndex ? styles.tabActive : ''}`}
                            >
                                {row.layer}
                            </button>
                        ))}
                    </div>

                    {/* Every layer's standards are in the HTML once (search engines read them all); only the active
                        one shows. Re-showing a panel restarts its fade-in, as re-keying it did. On phones the
                        stylesheet dissolves this wrapper and the tab list so the open panel lands under its tab. */}
                    <div className={styles.desktopPanelWrap}>
                        {rows.map((row, i) => (
                            <div
                                key={row.layer}
                                className={styles.panel}
                                role="tabpanel"
                                id={`compliance-panel-${i}`}
                                aria-labelledby={`compliance-tab-${i}`}
                                hidden={i !== activeIndex}
                                style={panelOrder(i)}
                            >
                                {row.standards.map((standard) => (
                                    <StandardCard key={standard.name} standard={standard} fallbackIcon={iconMap[row.iconType]} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </Container>
        </section>
    );
};

export default memo(ComplianceStandards);
